import {view, createView} from '@sail/ui';
import {
  Conversation,
  ConversationParticipant,
} from '@stripe-internal/conversations-client';
import {useEffect, useState} from 'react';
import {Subscription} from 'rxjs';
import {animate, AnimatePresence} from '@sail/engine';
import {useMessagesTopAnchored} from '../helpers/useMessagesTopAnchored';
import {TypingRowNext} from '../Chat/Row/TypingRowNext';
import {ANIMATION_DURATION} from '../index';

type Props = {
  conversation: Conversation;
  animated?: boolean;
};

export const TypingIndicatorNext = createView(
  ({conversation, animated, ...props}: Props) => {
    const [typers, setTypers] = useState<ConversationParticipant[]>([]);
    const {isTopAnchoredEnabled} = useMessagesTopAnchored(conversation);
    const [tightGap, setTightGap] = useState<boolean>(false);

    useEffect(() => {
      const sub = new Subscription();
      sub.add(conversation.typingObservable().subscribe(setTypers));

      // We need to keep track if the latest message is from the agent
      // or not to adjust spacing
      sub.add(
        conversation.eventsObservable().subscribe((e) => {
          if (e.name === 'conversation-message-added') {
            setTightGap(e.message?.participant.role === 'agent');
          }
        }),
      );
      return () => sub.unsubscribe();
    }, [conversation]);

    const typingRows = typers
      .filter(
        (typer) => typer.id !== conversation.user.id && typer.role === 'agent',
      )
      .map((typer) => <TypingRowNext key={typer.displayName} />);

    const hidden = typingRows.length < 1;

    return (
      <AnimatePresence>
        {hidden ? null : (
          <view.div
            {...props}
            className="uh-conversations-new-design"
            css={{
              flexGrow: 0,

              // If top anchoring is enabled, this is now handled in CSS in `GroupedMessagesRow`
              ...(!isTopAnchoredEnabled
                ? {
                    // If the latest message is also from the agent, we
                    // tighten up the spacing
                    marginTop: tightGap ? -20 : 0,
                  }
                : null),
            }}
            data-testid="typing-indicator"
            // Since this is just a typing indicator, we're setting the `aria-live` attribute to `off` so that
            // screen readers don't interrupt theirselves when new messages come in.
            aria-live="off"
            uses={
              animated
                ? [
                    animate.in({
                      duration: ANIMATION_DURATION,
                      transform: 'translateY(calc(100% * 1))',
                      easing: 'ease-out',
                    }),
                  ]
                : []
            }
          >
            {typingRows}
          </view.div>
        )}
      </AnimatePresence>
    );
  },
);
